import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Target, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';
import { AUDIT_QUESTIONS, PillarConfig } from '../types/alignment';
import { supabase } from '../lib/supabase';

export function AlignmentAuditDiagnostic() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditId, selectedArea } = location.state || {};
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCoreValues();
  }, []);

  const loadCoreValues = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('profiles')
      .select('core_values')
      .eq('id', user.id)
      .single();

    if (profile?.core_values) {
      setCoreValues(profile.core_values);
    }
  };

  if (!auditId || !selectedArea) {
    navigate('/alignment-audit/start');
    return null;
  }

  const currentQuestion = AUDIT_QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === AUDIT_QUESTIONS.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  const handleNext = async () => {
    if (isLastQuestion) {
      await handleComplete();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (isFirstQuestion) {
      navigate('/alignment-audit/select-area', {
        state: location.state
      });
    } else {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/');
      return;
    }

    const actionText = answers['action'];
    let taskId = null;

    if (actionText) {
      const { data: task } = await supabase
        .from('tasks')
        .insert({
          user_id: user.id,
          text: actionText,
          completed: false
        })
        .select()
        .single();

      taskId = task?.id;
    }

    const { error } = await supabase
      .from('alignment_audits')
      .update({
        celebrate: answers['celebrate'],
        learn: answers['learn'],
        goal: answers['goal'],
        fear: answers['fear'],
        values_vision: answers['values_vision'],
        action: answers['action'],
        completed_at: new Date().toISOString(),
        task_id: taskId
      })
      .eq('id', auditId);

    if (error) {
      console.error('Error completing audit:', error);
      setLoading(false);
      return;
    }

    navigate('/alignment-audit/complete', {
      state: { selectedArea, action: actionText }
    });
  };

  const getQuestionPrompt = () => {
    if (currentQuestion.id === 'values_vision' && coreValues.length > 0) {
      const valuesText = coreValues.length > 0
        ? coreValues.map((v, i) => {
            if (i === coreValues.length - 1 && coreValues.length > 1) return `and ${v}`;
            return v;
          }).join(', ')
        : '';

      return (
        <div className="space-y-2">
          <p className="text-xl text-gray-700">
            What do your values ({valuesText}) say to do regarding your goal? Create a list.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {coreValues.map((value, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-teal-100 text-teal-800 font-semibold rounded-lg text-sm"
              >
                {value}
              </span>
            ))}
          </div>
        </div>
      );
    }
    return <p className="text-xl text-gray-700">{currentQuestion.prompt}</p>;
  };

  return (
    <ProtectedLayout>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-full mb-4">
              <Target className="w-8 h-8 text-white" />
            </div>
            <div className="mb-2 text-sm font-semibold text-teal-600">
              Question {currentQuestionIndex + 1} of {AUDIT_QUESTIONS.length}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              USING THE ALIGNMENT ENGINE TO IMPROVE YOUR {(selectedArea as PillarConfig).name.toUpperCase()} SYSTEM
            </h1>
          </div>

          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all"
                style={{ width: `${((currentQuestionIndex + 1) / AUDIT_QUESTIONS.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                {currentQuestion.title}
                {answers[currentQuestion.field] && (
                  <CheckCircle2 className="w-6 h-6 text-teal-600" />
                )}
              </h2>
              {getQuestionPrompt()}
            </div>

            <textarea
              value={answers[currentQuestion.field] || ''}
              onChange={(e) => setAnswers(prev => ({
                ...prev,
                [currentQuestion.field]: e.target.value
              }))}
              placeholder={currentQuestion.placeholder}
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex justify-between items-center">
            <Button
              onClick={handleBack}
              variant="outline"
              disabled={loading}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={!answers[currentQuestion.field]?.trim() || loading}
              className="px-8 py-4 text-lg"
            >
              {isLastQuestion ? 'Complete Audit' : 'Next'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </ProtectedLayout>
  );
}
